<script lang="ts">
	import { browser } from '$app/env';
	import HttpMethodBadge, { normalizeApiLog } from '#lib/modern/HttpMethodBadge.svelte';
	import { apiLogStore } from '#lib/stores/api.js';
	import { t } from '#lib/stores/i18n.js';
	import { toastSuccess } from '#lib/stores/toast.js';
	import { ChevronDown, ChevronUp, Copy } from '@lucide/svelte';
	import { slide } from 'svelte/transition';

	let expandedId = $state<string | null>(null);
	let expandedBlocks = $state<Record<string, boolean>>({});

	function toggleExpand(id: string) {
		expandedId = expandedId === id ? null : id;
	}

	async function copyText(text: string) {
		if (!text) return;
		try {
			if (browser) {
				await navigator.clipboard.writeText(text);
			}
			toastSuccess($t('clipboard.copied'));
		} catch {
			// Fallback silently if clipboard permissions are restricted
		}
	}

	function handleToggleBlock(blockId: string, content: string) {
		const willExpand = !expandedBlocks[blockId];
		expandedBlocks[blockId] = willExpand;
		if (willExpand) {
			copyText(content);
		}
	}

	function formatDuration(ms?: number): string {
		if (ms === undefined) return '';
		if (ms >= 1000) {
			return `${(ms / 1000).toFixed(1)}s`;
		}
		return `${ms}ms`;
	}

	function isContentLong(content: string): boolean {
		return content.split('\n').length > 6 || content.length > 250;
	}
</script>

{#snippet codeBlock(
	title: string,
	content: string,
	blockId: string,
	isErrorVariant: boolean = false
)}
	{@const isLong = isContentLong(content)}
	{@const isBlockExpanded = !!expandedBlocks[blockId]}
	<div class="space-y-1">
		<div class="flex items-center justify-between">
			<span
				class="text-3xs font-semibold uppercase tracking-wider {isErrorVariant
					? 'text-red-600 dark:text-red-400 font-bold'
					: title === $t('diagnostics.api.response')
						? 'text-emerald-600 dark:text-emerald-400 font-semibold'
						: 'text-surface-on/50 dark:text-slate-400'}"
			>
				{title}
			</span>
			<div class="flex items-center gap-2">
				<button
					type="button"
					class="text-3xs font-semibold text-surface-on/60 hover:text-surface-on dark:text-slate-400 dark:hover:text-slate-200 flex items-center gap-1 cursor-pointer transition-colors"
					onclick={(e) => {
						e.stopPropagation();
						copyText(content);
					}}
					title={$t('diagnostics.copy_json')}
				>
					<Copy class="w-3 h-3" />
					<span>{$t('diagnostics.copy_json')}</span>
				</button>
				{#if isLong}
					<button
						type="button"
						class="text-3xs font-semibold {isErrorVariant
							? 'text-red-400'
							: 'text-primary'} hover:underline flex items-center gap-0.5 cursor-pointer"
						onclick={(e) => {
							e.stopPropagation();
							handleToggleBlock(blockId, content);
						}}
					>
						<span
							>{isBlockExpanded
								? $t('diagnostics.api.show_less')
								: $t('diagnostics.api.show_more')}</span
						>
						{#if isBlockExpanded}
							<ChevronUp class="w-3 h-3" />
						{:else}
							<ChevronDown class="w-3 h-3" />
						{/if}
					</button>
				{/if}
			</div>
		</div>
		<!-- svelte-ignore a11y_click_events_have_key_events -->
		<!-- svelte-ignore a11y_no_static_element_interactions -->
		<div
			class="rounded-xl {isErrorVariant
				? 'bg-[#180a0a] text-red-100 border border-red-500/40'
				: 'bg-black text-slate-200 border border-white/10'} p-2.5 text-2xs font-mono {isLong &&
			!isBlockExpanded
				? 'max-h-36 overflow-hidden relative cursor-pointer hover:border-white/30 transition-colors'
				: 'max-h-[50vh] overflow-y-auto'} shadow-inner group"
			onclick={() => {
				if (isLong && !isBlockExpanded) {
					handleToggleBlock(blockId, content);
				}
			}}
		>
			<pre
				class="whitespace-pre-wrap break-all select-all font-mono leading-relaxed">{content}</pre>
			{#if isLong && !isBlockExpanded}
				<div
					class="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t {isErrorVariant
						? 'from-[#180a0a] via-[#180a0a]/90'
						: 'from-black via-black/90'} to-transparent flex items-end justify-center pb-1.5 pointer-events-none"
				>
					<span
						class="text-3xs font-semibold {isErrorVariant
							? 'text-red-400'
							: 'text-slate-300'} group-hover:text-primary flex items-center gap-1 transition-colors"
					>
						{$t('diagnostics.api.show_more')}
						<ChevronDown class="w-3 h-3" />
					</span>
				</div>
			{/if}
		</div>
	</div>
{/snippet}

<div class="space-y-4">
	<div class="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
		{#if $apiLogStore.length === 0}
			<div
				class="py-12 text-center text-xs text-surface-on/50 dark:text-slate-400 border border-dashed border-border/60 dark:border-white/10 rounded-2xl"
			>
				{$t('diagnostics.socket.no_api_logs')}
			</div>
		{:else}
			{#each [...$apiLogStore].reverse() as log, index (index)}
				{@const item = normalizeApiLog(log)}
				{@const logId = item.id || `api-log-${index}`}
				{@const isError =
					item.isError ||
					(item.status !== undefined && (item.status >= 400 || item.status === 0))}
				{@const isSuccess =
					!isError &&
					item.status !== undefined &&
					item.status >= 200 &&
					item.status < 400}
				{@const isExpanded = expandedId === logId}
				{@const hasDetails =
					(item.payload !== undefined && item.payload !== null) ||
					(item.response !== undefined && item.response !== null)}

				<div
					class="rounded-xl border border-border/60 dark:border-white/10 border-l-[3.5px] {isError
						? 'border-l-red-500 dark:border-l-red-400 bg-red-500/[0.02]'
						: isSuccess
							? 'border-l-emerald-500 dark:border-l-emerald-400 bg-surface-on/[0.02] dark:bg-white/[0.02]'
							: 'border-l-blue-500 dark:border-l-blue-400 bg-surface-on/[0.02] dark:bg-white/[0.02]'} transition-all text-xs flex flex-col overflow-hidden"
				>
					<!-- Clickable full-card header button -->
					<button
						type="button"
						class="w-full p-3 flex items-center justify-between gap-3 cursor-pointer select-none font-mono text-left hover:bg-surface-on/[0.05] dark:hover:bg-white/[0.05] transition-colors focus:outline-none"
						onclick={() => toggleExpand(logId)}
						aria-expanded={isExpanded}
					>
						<div class="flex items-center gap-2.5 min-w-0 flex-1">
							{#if item.method}
								<HttpMethodBadge method={item.method} />
							{/if}
							<span
								class="break-all font-semibold text-surface-on dark:text-slate-100 truncate"
							>
								{item.url}
							</span>
							{#if item.status !== undefined}
								<span
									class="px-1.5 py-0.5 rounded text-2xs font-bold font-mono shrink-0 {isError
										? 'bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/20'
										: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'}"
								>
									{item.status === 0 ? 'ERR' : item.status}
								</span>
							{/if}
						</div>
						<div
							class="flex items-center gap-2.5 text-surface-on/50 dark:text-slate-400 text-2xs shrink-0"
						>
							{#if item.durationMs !== undefined}
								<span class="font-semibold">{formatDuration(item.durationMs)}</span>
							{/if}
							{#if item.timestamp}
								<span>
									{item.timestamp.includes('T')
										? item.timestamp.split('T')[1]?.slice(0, 8)
										: item.timestamp}
								</span>
							{/if}
							<ChevronDown
								class="w-4 h-4 transition-transform duration-200 {isExpanded
									? 'rotate-180'
									: ''}"
							/>
						</div>
					</button>

					<!-- Expanded Area (Payload & Response) -->
					{#if isExpanded}
						<div
							transition:slide={{ duration: 200 }}
							class="px-3 pb-3 pt-3 border-t border-border/40 dark:border-white/10 space-y-3 font-mono text-xs"
						>
							{#if !hasDetails}
								<div class="text-2xs text-surface-on/50 dark:text-slate-400 py-1">
									{$t('diagnostics.api.no_details')}
								</div>
							{/if}

							{#if item.payload !== undefined && item.payload !== null}
								{@render codeBlock(
									$t('diagnostics.api.payload'),
									JSON.stringify(item.payload, null, 2),
									`payload-${logId}`,
									false
								)}
							{/if}

							{#if item.response !== undefined && item.response !== null}
								{@render codeBlock(
									isError
										? $t('diagnostics.api.error_response')
										: $t('diagnostics.api.response'),
									typeof item.response === 'string'
										? item.response
										: JSON.stringify(item.response, null, 2),
									`response-${logId}`,
									isError
								)}
							{/if}
						</div>
					{/if}
				</div>
			{/each}
		{/if}
	</div>
</div>
